import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Expand } from "lucide-react";
import TopBar from "@/components/TopBar";
import Footer from "@/components/Footer";
import WhatsAppFAB from "@/components/WhatsAppFAB";
import BackgroundMedia from "@/components/BackgroundMedia";
import Lightbox from "@/components/Lightbox";
import Seo, { composeTitle, truncateForMeta } from "@/components/Seo";
import { Container, SectionLabel, ButtonLux } from "@/components/primitives";
import { useSettings } from "@/context/SettingsContext";
import { api, mediaSrc } from "@/lib/api";

export default function ProjectDetail() {
  const { id } = useParams();
  const { settings } = useSettings();
  const [p, setP] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    if (id) api.getProject(id).then(setP).catch(() => {});
  }, [id]);

  const gallery = useMemo(() => {
    if (!p) return [];
    return (Array.isArray(p.gallery_images) ? p.gallery_images : []).filter((u) => typeof u === "string" && u.trim());
  }, [p]);

  const total = gallery.length;
  const hasGallery = total > 0;

  if (!p) {
    return <div className="min-h-screen bg-lux-bg flex items-center justify-center text-lux-muted">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-lux-bg">
      <Seo
        title={composeTitle(p.seo_title || p.name, settings.seo.default_title, settings.seo.title_suffix)}
        description={truncateForMeta(p.seo_description || p.summary || settings.seo.default_description)}
        path={`/projects/${p.id}`}
        imageUrl={p.image_url ? mediaSrc(p.image_url) : undefined}
        type="article"
      />
      <TopBar />
      <div className="relative w-full h-[520px] md:h-[720px] overflow-hidden bg-lux-line">
        <BackgroundMedia videoUrl={p.cover_video_url} imageUrl={p.image_url} testId="project-detail-media" />
      </div>
      <Container className="pt-10 md:pt-16 pb-24">
        <SectionLabel>WITH · {p.designer.toUpperCase()}</SectionLabel>
        <h1 className="font-serif text-lux-ink font-light tracking-tight leading-none mt-3 text-4xl md:text-7xl">{p.name}</h1>
        {p.location ? <p className="text-[11px] tracking-[0.2em] uppercase text-lux-muted mt-3">{p.location}</p> : null}
        <p className="font-serif italic text-lux-ink font-light leading-relaxed mt-6 text-xl md:text-2xl max-w-[760px]">{p.summary}</p>
        {p.description ? <p className="text-lux-sec font-light leading-relaxed mt-7 text-base md:text-[17px] max-w-[760px] whitespace-pre-line">{p.description}</p> : null}

        {hasGallery && (
          <div className="mt-12 md:mt-14" data-testid="project-gallery">
            <SectionLabel>GALLERY · {String(total).padStart(2, "0")} IMAGES</SectionLabel>
            <h2 className="font-serif text-2xl md:text-3xl text-lux-ink font-light tracking-tight mt-2.5 mb-6">A closer look.</h2>
            <button
              type="button"
              onClick={() => setLightboxOpen(true)}
              data-testid="gallery-frame"
              aria-label={`View gallery — ${total} image${total > 1 ? "s" : ""}`}
              className="relative block w-full max-w-[1100px] h-80 md:h-[560px] overflow-hidden bg-lux-line group"
            >
              <img src={mediaSrc(gallery[0])} alt={`${p.name} — gallery`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]" data-testid="gallery-frame-img" />
              <span className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white"><Expand size={15} strokeWidth={1.6} /></span>
              {total > 1 && (
                <span className="absolute bottom-3 left-3 px-2.5 py-1 bg-black/50 backdrop-blur-sm text-white text-[10px] tracking-[0.25em] tabular-nums" data-testid="gallery-counter">
                  01 / {String(total).padStart(2, "0")}
                </span>
              )}
            </button>
            <p className="mt-3 text-[11px] tracking-[0.2em] uppercase text-lux-muted">Tap the image to view the full gallery</p>
          </div>
        )}

        <div className="mt-12 md:mt-14">
          <ButtonLux label="Discuss a Similar Project" to="/contact" testId="project-cta" />
        </div>
      </Container>
      <Footer />
      <WhatsAppFAB />
      {lightboxOpen && hasGallery && (
        <Lightbox images={gallery} startIndex={0} alt={p.name} onClose={() => setLightboxOpen(false)} testId="project-lightbox" />
      )}
    </div>
  );
}
